import React from 'react';
import styled from 'styled-components';
import PageTemplate from '../components/templates/PageTemplate';
import { MobileHeaderType } from '../components/layout/ConfigurableMobileHeader';

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
`;

const StatTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 8px 0;
`;

const StatValue = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const StatChange = styled.div<{ positive?: boolean }>`
  color: ${({ positive, theme }) => positive ? '#4caf50' : '#f44336'};
  font-size: 14px;
  display: flex;
  align-items: center;
  
  &::before {
    content: '${({ positive }) => positive ? '↑' : '↓'}';
    margin-right: 4px;
  }
`;

const RecentActivityContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 24px 0;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.primary}33;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  color: ${({ theme }) => theme.colors.primary};
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
  margin-bottom: 4px;
`;

const ActivityTime = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
`;

const ChartContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: 12px;
  padding: 24px;
  height: 300px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Dashboard: React.FC = () => {
  return (
    <PageTemplate 
      title="Dashboard"
      mobileHeaderType={MobileHeaderType.FULL}
      mobileHeaderTitle="Dashboard"
    >
      <StatsContainer>
        <StatCard>
          <StatTitle>Total Views</StatTitle>
          <StatValue>24.5K</StatValue>
          <StatChange positive>12.3% vs last week</StatChange>
        </StatCard>
        <StatCard>
          <StatTitle>Engagement Rate</StatTitle>
          <StatValue>8.7%</StatValue>
          <StatChange positive>2.1% vs last week</StatChange>
        </StatCard>
        <StatCard>
          <StatTitle>Followers</StatTitle>
          <StatValue>1,254</StatValue>
          <StatChange positive>5.6% vs last week</StatChange>
        </StatCard>
        <StatCard>
          <StatTitle>Conversion Rate</StatTitle>
          <StatValue>3.2%</StatValue>
          <StatChange>0.5% vs last week</StatChange>
        </StatCard>
      </StatsContainer>

      <ChartContainer>
        Performance chart will be displayed here
      </ChartContainer>

      <RecentActivityContainer>
        <SectionTitle>Recent Activity</SectionTitle>
        <ActivityList>
          <ActivityItem>
            <ActivityIcon>📹</ActivityIcon>
            <ActivityContent>
              <ActivityTitle>New video submitted to Enterprise A</ActivityTitle>
              <ActivityTime>2 hours ago</ActivityTime>
            </ActivityContent>
          </ActivityItem>
          <ActivityItem>
            <ActivityIcon>👍</ActivityIcon>
            <ActivityContent>
              <ActivityTitle>Enterprise B accepted your video</ActivityTitle>
              <ActivityTime>Yesterday</ActivityTime>
            </ActivityContent>
          </ActivityItem>
          <ActivityItem>
            <ActivityIcon>💬</ActivityIcon>
            <ActivityContent>
              <ActivityTitle>New comment on your video from Enterprise C</ActivityTitle>
              <ActivityTime>2 days ago</ActivityTime>
            </ActivityContent>
          </ActivityItem>
          <ActivityItem>
            <ActivityIcon>🔔</ActivityIcon>
            <ActivityContent>
              <ActivityTitle>Enterprise D posted a new opportunity</ActivityTitle>
              <ActivityTime>3 days ago</ActivityTime>
            </ActivityContent>
          </ActivityItem>
        </ActivityList>
      </RecentActivityContainer>
    </PageTemplate>
  );
};

export default Dashboard;
